import { useState } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { useTranslation } from "react-i18next";
import { BiSolidError } from "react-icons/bi";
import { FiBookOpen, FiEdit, FiEye } from "react-icons/fi";
import { MdOutlineEdit } from "react-icons/md";
import { IoCloseSharp } from "react-icons/io5";
import { useSelector } from "react-redux";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import ActionMenu from "../ActionMenu";
import { toast } from "react-toastify";
import { server } from "../../App";
import { useDeleteVehicleMutation } from "../../store/api/vendor/vehiclesSlice";
import AdminPagination from "../pagination/AdminPagination";
import SkeletonLoading from "../SkeletonLoading";
import Tooltip from "../Tooltip";
import BusCharter from "../Model/reservation/BusCharter";
import BusingSubscriptions from "../Model/reservation/BusingSubscription";

// Get status badge class
export const getStatusBadge = (status) => {
  switch (status) {
    case "available":
      return "status available";
    case "scheduled":
      return "status available";
    case "booked":
      return "status booked";
    case "pending":
      return "status pending";
    case "verified":
      return "status verified";
    case "maintenance":
      return "status maintenance";
    case "inactive":
      return "status inactive";
    case "inspection":
      return "status inspection";
    case "rejected":
      return "status rejected";
    case "cancelled":
      return "status cancelled";
    case "scheduled":
      return "status scheduled";
    case "picked_up":
      return "status picked_up";
    case "dropped_off":
      return "status dropped_off";
    case "absent":
      return "status absent";
    case "completed":
      return "status completed";
    case "started":
      return "status started";
    case "in_progress":
      return "status in_progress";
    case "no_show":
      return "status no_show";
  }
};

// Get status text
export const getStatusText = (status) => {
  switch (status) {
    case "available":
      return "Available";
    case "booked":
      return "Booked";
    case "scheduled":
      return "Scheduled";
    case "pending":
      return "Pending";
    case "verified":
      return "Verified";
    case "maintenance":
      return "Maintenance";
    case "inactive":
      return "Inactive";
    case "inspection":
      return "Inspection";
    case "rejected":
      return "Rejected";
    case "cancelled":
      return "Cancelled";
    case "admin":
      return "Super Aadmin";
    case "completed":
      return "Completed";
    case "started":
      return "Started";
    // default:
    //   return "Unavailable";
  }
};

// Get condition badge
const getConditionBadge = (condition) => {
  switch (condition?.toLowerCase()) {
    case "good":
      return <span className="badge bg-success">Good</span>;
    case "fair":
      return <span className="badge bg-warning">Fair</span>;
    case "poor":
      return <span className="badge bg-danger">Poor</span>;
    default:
      return <span className="badge bg-secondary">{condition || "N/A"}</span>;
  }
};

const VehiclesTable = ({
  vehicles,
  isLoading,
  pagination,
  setPagination,
  isBooking = false,
}) => {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.UserReducer);
  const [showDelete, setShowDelete] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [show, setShow] = useState(false);
  const [show2, setShow2] = useState(false);
  const [show3, setShow3] = useState(false);

  const [selectedVehicle, setSelectedVehicle] = useState(null);

  const [id, setId] = useState("");
  const { role, slug } = useParams();
  const navigate = useNavigate();

  // Get Role From React Redux
  const isAdmin = user?.role === "super_admin";

  // delete query
  const [deleteVehicle, { isLoading: isDeleteLoading }] =
    useDeleteVehicleMutation();

  // Delete modal show function
  const handleShowDelete = (id) => {
    setShowDelete(true);
    setId(id);
  };

  // Detail modal show function
  const handleShowDetail = (vehicle) => {
    setSelectedVehicle(vehicle);
    setShowDetail(true);
  };
  // show booking model
  const handleShow = (data) => {
    setSelectedVehicle(data);
    setShow(true);
  };
  const handleShow2 = (data) => {
    setSelectedVehicle(data);
    setShow2(true);
  };

  const handleClose = () => {
    setShow(false);
    setShow2(false);
    setShow3(false);
    setShowDetail(false);
    setSelectedVehicle(null);
  };

  // delete function
  const handleDelete = async () => {
    toast.dismiss();
    try {
      const response = await deleteVehicle(id).unwrap();
      if (response.statusCode === 200) {
        toast.success(response.message);
        setShowDelete(false);
        setId("");
      }
    } catch (err) {
      const mesg = err.data?.message || "Something went wrong";
      toast.error(mesg);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    });
  };

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const handleLimitChange = (limit) => {
    setPagination((prev) => ({
      ...prev,
      limit,
      page: 1,
    }));
  };

  return (
    <>
      <div className="table m-0">
        <table>
          <thead>
            <tr>
              <th>{`${t("Sr")}. ${t("no")}.`}</th>
              <th>{t("image")}</th>
              <th>
                {t("vehicle")} {t("type")}
              </th>
              <th>
                {t("vehicle")} {t("license")} {t("plate")}
              </th>
              <th>
                {t("fare")} {t("rate")}
              </th>
              <th>
                {t("available")} {t("seaats")}
              </th>
              <th>{t("status")}</th>
              <th>{t("action")}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <SkeletonLoading length={8} />
            ) : vehicles.length > 0 ? (
              vehicles.map((item, i) => (
                <tr key={i}>
                  <td> {(pagination.page - 1) * pagination.limit + i + 1}</td>
                  <td>
                    <img
                      className="img-thumbnail"
                      src={`${server}${item?.vehicle_images[0]?.img}`}
                      alt="Vehicle"
                      style={{
                        width: "120px",
                        height: "80px",
                        borderRadius: "5px",
                      }}
                    />
                  </td>
                  <td>{item?.vehicle_type?.name}</td>
                  <td className="text-nowrap">{item.license_number}</td>
                  <td className="text-nowrap">
                    {formatCurrency(item?.fare_rate)}/hr
                  </td>
                  <td>{item?.seat || 0}</td>
                  <td>
                    <span className={getStatusBadge(item?.vehicle_status)}>
                      {getStatusText(item?.vehicle_status)}
                    </span>
                  </td>
                  <td>
                    <ActionMenu
                      actions={[
                        {
                          label: `${t("book")} ${t("now")}`,
                          icon: <FiBookOpen size={15} />,
                          onClick: () => {
                            if (
                              slug === "my-busing-subscriptions" ||
                              slug === "vendor-busing-subscriptions"
                            ) {
                              handleShow2(item);
                            } else {
                              handleShow(item);
                            }
                          },
                          hidden: !isBooking,
                        },
                        {
                          label: t("view"),
                          icon: <FiEye size={15} />,
                          onClick: () => handleShowDetail(item),
                        },
                        {
                          label: t("edit"),
                          icon: <MdOutlineEdit size={16} />,
                          onClick: () => navigate(`/vehicles/edit/${item.id}`),
                          hidden: isAdmin || isBooking,
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))
            ) : (
              <tr className="text-center">
                <td colSpan="12">
                  <div>
                    <span>
                      {t("no")} {t("vehicle")} {t("found")}
                    </span>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <AdminPagination
        totalPages={pagination.totalPages}
        currentPage={pagination.page}
        limit={pagination.limit}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
      />

      {/* Delete Confirmation Modal */}
      <Modal show={showDelete}>
        <Button className="closePopup" onClick={() => setShowDelete(false)}>
          <IoCloseSharp />
        </Button>
        <div className="popupBox p-3 p-md-5">
          <div>
            <div className="text-center mb-3 errorIcon">
              <BiSolidError />
            </div>
            <div className="text-center mb-4 deleteHd">
              {t("confirmDelete")}
            </div>
            <div className="text-center">
              <Button
                className="smallBtn cancel me-3"
                onClick={() => setShowDelete(false)}
              >
                {t("btns.cancel")}
              </Button>
              <Button
                className="smallBtn auto"
                onClick={handleDelete}
                disabled={isDeleteLoading}
              >
                {isDeleteLoading ? (
                  <div className="spinner-border" role="status">
                    <span className="sr-only p-0"></span>
                  </div>
                ) : (
                  t("btns.delete")
                )}
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Vehicle Detail Modal */}
      {showDetail && (
        <Modal
          show={showDetail}
          onHide={handleClose}
          size="lg"
          centered
          backdrop="static"
          className="detailModel"
        >
          <Modal.Header closeButton className="border-0">
            <Modal.Title className="h5">
              {" "}
              {`${t("vehicle")} ${t("details")}`}
            </Modal.Title>
          </Modal.Header>

          <Modal.Body
            style={{
              maxHeight: "calc(80vh - 120px)",
              overflowY: "auto",
            }}
          >
            {selectedVehicle && (
              <div className="p-2">
                {/* Images Gallery */}
                <div className="mb-4">
                  <h6 className="mb-3">{`${t("vehicle")} ${t("images")}`} </h6>
                  <div className="d-flex flex-wrap gap-3">
                    {selectedVehicle.vehicle_images?.map((img, index) => (
                      <img
                        key={index}
                        className="img-thumbnail"
                        src={`${server}${img.img}`}
                        alt={`${t("vehicle")} ${index + 1}`}
                        style={{
                          width: "120px",
                          height: "80px",
                          borderRadius: "8px",
                          objectFit: "cover",
                          border: "1px solid #ddd",
                        }}
                      />
                    ))}
                  </div>
                </div>

                <div className="row">
                  {/* Basic Information */}
                  <div className="col-md-6 mb-4">
                    <h6 className="border-bottom pb-2 mb-3">
                      {t("basic")} {t("information")}
                    </h6>
                    <div className="row mb-2">
                      <div className="col-6">
                        <strong className="text-muted">
                          {t("license")} {t("number")}:
                        </strong>
                      </div>
                      <div className="col-6">
                        {selectedVehicle.license_number}
                      </div>
                    </div>
                    <div className="row mb-2">
                      <div className="col-6">
                        <strong className="text-muted">
                          {t("registration")}:
                        </strong>
                      </div>
                      <div className="col-6">
                        {selectedVehicle.registration_number || "N/A"}
                      </div>
                    </div>
                    <div className="row mb-2">
                      <div className="col-6">
                        <strong className="text-muted">{t("vin")}:</strong>
                      </div>
                      <div className="col-6">{selectedVehicle.vin}</div>
                    </div>
                    <div className="row mb-2">
                      <div className="col-6">
                        <strong className="text-muted">
                          {t("current")} {t("mileage")}:
                        </strong>
                      </div>
                      <div className="col-6">
                        {selectedVehicle.current_mileage || "N/A"}
                      </div>
                    </div>
                  </div>

                  {/* Specifications */}
                  <div className="col-md-6 mb-4">
                    <h6 className="border-bottom pb-2 mb-3">
                      {t("specifications")}
                    </h6>
                    <div className="row mb-2">
                      <div className="col-6">
                        <strong className="text-muted">{t("make")}:</strong>
                      </div>
                      <div className="col-6">{selectedVehicle.make?.name}</div>
                    </div>
                    <div className="row mb-2">
                      <div className="col-6">
                        <strong className="text-muted">{t("model")}:</strong>
                      </div>
                      <div className="col-6">{selectedVehicle.model?.name}</div>
                    </div>
                    <div className="row mb-2">
                      <div className="col-6">
                        <strong className="text-muted">{t("year")}:</strong>
                      </div>
                      <div className="col-6">{selectedVehicle.year?.year}</div>
                    </div>
                    <div className="row mb-2">
                      <div className="col-6">
                        <strong className="text-muted">{t("type")}:</strong>
                      </div>
                      <div className="col-6">
                        {selectedVehicle.vehicle_type?.name}
                      </div>
                    </div>
                    <div className="row mb-2">
                      <div className="col-6">
                        <strong className="text-muted">{t("color")}:</strong>
                      </div>
                      <div className="col-6">
                        <span
                          className="color-badge d-inline-block px-2 py-1 rounded"
                          style={{
                            backgroundColor:
                              selectedVehicle.color?.name?.toLowerCase(),
                            color: ["white", "yellow", "lightblue"].includes(
                              selectedVehicle.color?.name?.toLowerCase(),
                            )
                              ? "#000"
                              : "#fff",
                          }}
                        >
                          {selectedVehicle.color?.name}
                        </span>
                      </div>
                    </div>
                    <div className="row mb-2">
                      <div className="col-6">
                        <strong className="text-muted">{t("seats")}:</strong>
                      </div>
                      <div className="col-6">{selectedVehicle.seat}</div>
                    </div>
                  </div>

                  {/* Status & Conditions */}
                  <div className="col-md-6 mb-4">
                    <h6 className="border-bottom pb-2 mb-3">
                      {t("status")} {t("and")} {t("conditions")}
                    </h6>
                    <div className="row mb-2">
                      <div className="col-6">
                        <strong className="text-muted">{t("status")}:</strong>
                      </div>
                      <div className="col-6">
                        <span
                          className={getStatusBadge(
                            selectedVehicle.vehicle_status,
                          )}
                        >
                          {getStatusText(selectedVehicle.vehicle_status)}
                        </span>
                      </div>
                    </div>
                    <div className="row mb-2">
                      <div className="col-6">
                        <strong className="text-muted">
                          {t("fare")} {t("rate")}:
                        </strong>
                      </div>
                      <div className="col-6">
                        {formatCurrency(selectedVehicle.fare_rate)}
                      </div>
                    </div>
                    <div className="row mb-2">
                      <div className="col-6">
                        <strong className="text-muted">
                          {t("vehicle")} {t("condition")}:
                        </strong>
                      </div>
                      <div className="col-6">
                        {getConditionBadge(selectedVehicle.vehicle_condition)}
                      </div>
                    </div>
                    <div className="row mb-2">
                      <div className="col-6">
                        <strong className="text-muted">
                          {t("tyre")} {t("condition")}:
                        </strong>
                      </div>
                      <div className="col-6">
                        {selectedVehicle.tyre_condition}
                      </div>
                    </div>
                  </div>

                  {/* Owner & Documents */}
                  <div className="col-md-6 mb-4">
                    <h6 className="border-bottom pb-2 mb-3">
                      {t("owner")}
                      {/* {t("and")} {t("documents")} */}
                    </h6>
                    <div className="row mb-2">
                      <div className="col-6">
                        <strong className="text-muted">{t("owner")}:</strong>
                      </div>
                      <div className="col-6">
                        {selectedVehicle.owner?.name} (
                        {selectedVehicle.owner?.role})
                      </div>
                    </div>
                    <div className="row mb-2">
                      <div className="col-6">
                        <strong className="text-muted">{t("email")}:</strong>
                      </div>
                      <div className="col-6">
                        {selectedVehicle.owner?.email}
                      </div>
                    </div>
                  </div>

                  {/* <div className="row mb-4">
                    <div className="col-12">
                      <h6 className="border-bottom pb-2 mb-3">
                        {t("documents")}
                      </h6>
                      <div className="row g-3">
                        <div className="col-md-6">
                          <div className="card h-100 border">
                            <div className="card-body">
                              <div className="d-flex align-items-center mb-3">
                                <div className="bg-primary bg-opacity-10 p-2 rounded me-3">
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    fill="currentColor"
                                    className="bi bi-shield-check text-primary"
                                    viewBox="0 0 16 16"
                                  >
                                    <path d="M5.338 1.59a61.44 61.44 0 0 0-2.837.856.481.481 0 0 0-.328.39c-.554 4.157.726 7.19 2.253 9.188a10.725 10.725 0 0 0 2.287 2.233c.346.244.652.42.893.533.12.057.218.095.293.118a.55.55 0 0 0 .101.025.615.615 0 0 0 .1-.025c.076-.023.174-.061.294-.118.24-.113.547-.29.893-.533a10.726 10.726 0 0 0 2.287-2.233c1.527-1.997 2.807-5.031 2.253-9.188a.48.48 0 0 0-.328-.39c-.651-.213-1.75-.56-2.837-.855C9.552 1.29 8.531 1.067 8 1.067c-.53 0-1.552.223-2.662.524zM5.072.56C6.157.265 7.31 0 8 0s1.843.265 2.928.56c1.11.3 2.229.655 2.887.87a1.54 1.54 0 0 1 1.044 1.262c.596 4.477-.787 7.795-2.465 9.99a11.775 11.775 0 0 1-2.517 2.453 7.159 7.159 0 0 1-1.048.625c-.28.132-.581.24-.829.24s-.548-.108-.829-.24a7.158 7.158 0 0 1-1.048-.625 11.777 11.777 0 0 1-2.517-2.453C1.928 10.487.545 7.169 1.141 2.692A1.54 1.54 0 0 1 2.185 1.43 62.456 62.456 0 0 1 5.072.56z" />
                                    <path d="M10.854 5.146a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708 0l-1.5-1.5a.5.5 0 1 1 .708-.708L7.5 7.793l2.646-2.647a.5.5 0 0 1 .708 0z" />
                                  </svg>
                                </div>
                                <h6 className="card-title mb-0">
                                  {t("insurance")}
                                </h6>
                              </div>

                              {selectedVehicle.insurance_documents ? (
                                <>
                                  <p className="card-text small text-muted mb-3">
                                    {t("insuranceAvailable")}
                                  </p>
                                  <a
                                    href={`${server}${selectedVehicle.insurance_documents}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-primary w-100 d-flex align-items-center justify-content-center gap-2"
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="16"
                                      height="16"
                                      fill="currentColor"
                                      className="bi bi-file-text"
                                      viewBox="0 0 16 16"
                                    >
                                      <path d="M5 4a.5.5 0 0 0 0 1h6a.5.5 0 0 0 0-1H5zm-.5 2.5A.5.5 0 0 1 5 6h6a.5.5 0 0 1 0 1H5a.5.5 0 0 1-.5-.5zM5 8a.5.5 0 0 0 0 1h6a.5.5 0 0 0 0-1H5zm0 2a.5.5 0 0 0 0 1h3a.5.5 0 0 0 0-1H5z" />
                                      <path d="M2 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2zm10-1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1z" />
                                    </svg>
                                    {t("open")} {t("insurance")} {t("document")}
                                  </a>
                                </>
                              ) : (
                                <>
                                  <p className="card-text small text-muted mb-3">
                                    {t("noInsuranceUploaded")}
                                  </p>
                                  <button
                                    className="btn btn-outline-secondary w-100"
                                    disabled
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="16"
                                      height="16"
                                      fill="currentColor"
                                      className="bi bi-exclamation-circle"
                                      viewBox="0 0 16 16"
                                    >
                                      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                                      <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 4.995z" />
                                    </svg>
                                    {t("document")} {t("not")} {t("available")}
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="card h-100 border">
                            <div className="card-body">
                              <div className="d-flex align-items-center mb-3">
                                <div className="bg-success bg-opacity-10 p-2 rounded me-3">
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    fill="currentColor"
                                    className="bi bi-clipboard-check text-success"
                                    viewBox="0 0 16 16"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M10.854 7.146a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708 0l-1.5-1.5a.5.5 0 1 1 .708-.708L7.5 9.793l2.646-2.647a.5.5 0 0 1 .708 0z"
                                    />
                                    <path d="M4 1.5H3a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v1h1a1 1 0 0 1 1 1V14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1h1v-1z" />
                                    <path d="M9.5 1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5h3zm-3-1A1.5 1.5 0 0 0 5 1.5v1A1.5 1.5 0 0 0 6.5 4h3A1.5 1.5 0 0 0 11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3z" />
                                  </svg>
                                </div>
                                <h6 className="card-title mb-0">
                                  {t("permit")}
                                </h6>
                              </div>

                              {selectedVehicle.permit_documents ? (
                                <>
                                  <p className="card-text small text-muted mb-3">
                                    {t("permitAvailable")}
                                  </p>
                                  <a
                                    href={`${server}${selectedVehicle.permit_documents}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-success w-100 d-flex align-items-center justify-content-center gap-2"
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="16"
                                      height="16"
                                      fill="currentColor"
                                      className="bi bi-file-check"
                                      viewBox="0 0 16 16"
                                    >
                                      <path d="M10.854 6.146a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708 0l-1.5-1.5a.5.5 0 1 1 .708-.708L7.5 8.793l2.646-2.647a.5.5 0 0 1 .708 0z" />
                                      <path d="M4 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H4zm0 1h8a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1z" />
                                    </svg>
                                    {t("open")} {t("permit")} {t("document")}
                                  </a>
                                </>
                              ) : (
                                <>
                                  <p className="card-text small text-muted mb-3">
                                    {t("noPermitUploaded")}
                                  </p>
                                  <button
                                    className="btn btn-outline-secondary w-100"
                                    disabled
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="16"
                                      height="16"
                                      fill="currentColor"
                                      className="bi bi-exclamation-circle"
                                      viewBox="0 0 16 16"
                                    >
                                      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                                      <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 4.995z" />
                                    </svg>
                                    {t("document")} {t("not")} {t("available")}
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div> */}

                  {/* Description */}
                  {selectedVehicle.description && (
                    <div className="col-12 mb-4">
                      <h6 className="border-bottom pb-2 mb-3">
                        {t("description")}
                      </h6>
                      <div className="p-3 bg-light rounded">
                        {selectedVehicle.description}
                      </div>
                    </div>
                  )}

                  {/* Created Info */}
                  <div className="col-12">
                    <div className="row text-muted small">
                      <div className="col-6">
                        <strong>{t("created")}:</strong>{" "}
                        {new Date(
                          selectedVehicle.created_at,
                        ).toLocaleDateString()}
                      </div>
                      <div className="col-6">
                        <strong>{t("updated")}:</strong>{" "}
                        {new Date(
                          selectedVehicle.updated_at,
                        ).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Modal.Body>

          <Modal.Footer className="border-0">
            <Button variant="secondary" onClick={() => setShowDetail(false)}>
              {t("close")}
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* bus-charter-reservations model for vendor/school */}
      {show && (
        <Modal
          show={show}
          onHide={handleClose}
          size="lg"
          centered
          backdrop="static"
        >
          <Modal.Header closeButton className="border-0">
            <Modal.Title className="h5">{t("reservations")}</Modal.Title>
          </Modal.Header>
          <BusCharter selectedVehicle={selectedVehicle} onClose={handleClose} />
        </Modal>
      )}

      {/* busing-subscriptions model */}
      {show2 && (
        <Modal
          show={show2}
          onHide={handleClose}
          size="lg"
          centered
          backdrop="static"
        >
          <Modal.Header closeButton className="border-0">
            <Modal.Title className="h5">{t("reservations")}</Modal.Title>
          </Modal.Header>
          <BusingSubscriptions
            selectedVehicle={selectedVehicle}
            onClose={handleClose}
          />
        </Modal>
      )}

      {/*add model */}
      {/* <Modal show={true}>
        <Button className="closePopup" onClick={handleModalClose}>
          <IoCloseSharp />
        </Button>
        <CreateModal
          title={`${t("maintenance")} ${t("and")} ${t("inspection")} ${
            selectedReservation?.id ? t("updated") : t("added")
          } ${t("successfully")}!`}
          text={
            selectedReservation?.id
              ? `${t("maintenance")} ${t("and")} ${t("inspection")} ${t(
                  "have",
                )} ${t("been")} ${t("updated")}.`
              : `${t("maintenance")} ${t("and")} ${t("inspection")} ${t(
                  "have",
                )} ${t("been")} ${t("added")}.`
          }
          onClose={handleClose}
        />
      </Modal> */}
      <style>
        {`
          .detailModel .modal-dialog {
            max-width: 900px;
          }

          .searchBox {
            width: 200px;
          }

          .color-badge {
            padding: 2px 8px;
            border-radius: 12px;
            font-size: 12px;
            font-weight: 500;
            display: inline-block;
            text-transform: capitalize;
          }

          /* Document Links Styling */
          .document-link {
            color: #0d6efd;
            text-decoration: none;
            padding: 6px 12px;
            border-radius: 6px;
            background-color: #f8f9fa;
            border: 1px solid #dee2e6;
            transition: all 0.2s;
          }

          .document-link:hover {
            background-color: #e9ecef;
            text-decoration: underline;
            color: #0a58ca;
          }

          /* Badge for document status */
          .document-badge {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            padding: 6px 12px;
            border-radius: 20px;
            font-size: 14px;
            font-weight: 500;
          }

          .document-badge.available {
            background-color: #d1fae5;
            color: #065f46;
            border: 1px solid #a7f3d0;
          }

          .document-badge.not-available {
            background-color: #f3f4f6;
            color: #6b7280;
            border: 1px solid #e5e7eb;
          }
        `}
      </style>
    </>
  );
};

export default VehiclesTable;
